<template>
  <div
    ref="containerRef"
    class="busca-global"
  >
    <div
      class="busca-global-input"
      :class="{ 'is-panel-open': painelAberto }"
    >
      <feather-icon
        icon="SearchIcon"
        size="16"
        class="busca-global-icon"
      />
      <b-form-input
        ref="inputBusca"
        v-model="termoBusca"
        class="busca-global-control"
        placeholder="Buscar cadastros, propostas, jornadas ou contratos..."
        autocomplete="off"
        @focus="abrirPainel"
        @keydown.down.prevent="navegarResultados('down')"
        @keydown.up.prevent="navegarResultados('up')"
        @keydown.enter.prevent="selecionarResultadoAtual"
        @keydown.esc.prevent="fecharPainel"
        @keydown.tab="fecharPainel"
      />
      <feather-icon
        v-if="termoBusca"
        icon="XIcon"
        size="16"
        class="busca-global-clear"
        @click="limparBusca"
      />
    </div>

    <div
      v-if="painelAberto"
      class="busca-global-panel"
      role="combobox"
      :aria-expanded="showResultados"
      aria-haspopup="listbox"
      aria-owns="busca-global-resultados"
    >
      <div class="busca-global-filtros">
        <span class="busca-global-label">Filtrar:</span>
        <button
          v-for="filtro in filtrosDisponiveis"
          :key="filtro.key"
          type="button"
          class="busca-filtro-btn"
          :class="{ 'is-active': isFiltroAtivo(filtro.key) }"
          :title="filtro.label"
          @click="toggleFiltro(filtro.key)"
        >
          <feather-icon
            :icon="filtro.icon"
            size="18"
          />
        </button>
      </div>

      <div class="busca-global-resultados">
        <div
          v-if="estado === estados.CARREGANDO"
          class="busca-global-status"
        >
          <b-spinner
            small
            variant="primary"
          />
          <span class="ml-1">Buscando...</span>
        </div>

        <div
          v-else-if="estado === estados.ERRO"
          class="busca-global-status busca-global-erro"
        >
          {{ mensagemErro }}
          <b-button
            size="sm"
            variant="outline-primary"
            class="ml-1"
            @click="executarBusca"
          >
            Tentar novamente
          </b-button>
        </div>

        <div
          v-else-if="estado === estados.VAZIO"
          class="busca-global-status"
        >
          Nenhum resultado encontrado.
        </div>

        <div
          v-else-if="estado === estados.RESULTADOS"
          class="busca-global-lista"
        >
          <ul
            id="busca-global-resultados"
            role="listbox"
            aria-label="Resultados da busca"
          >
            <template v-for="grupo in gruposComIndex">
              <li
                v-if="grupo.items.length"
                :key="`grupo-${grupo.tipo}`"
                class="busca-global-grupo-titulo"
              >
                {{ grupo.label }} ({{ grupo.items.length }})
              </li>
              <li
                v-for="item in grupo.items"
                :id="`resultado-${item.index}`"
                :key="item.uniqueId"
                role="option"
                :aria-selected="itemAtivoIndex === item.index"
                class="busca-global-item"
                :class="{ 'is-active': itemAtivoIndex === item.index }"
                @mouseenter="itemAtivoIndex = item.index"
                @mousedown.prevent="navegarPara(item.tipo, item.id)"
              >
                <template v-if="item.tipo === 'empresa'">
                  <div class="busca-global-item-titulo">
                    <span
                      v-for="(parte, idx) in partesDestaque(item.razaoSocial || item.nomeFantasia || 'Empresa')"
                      :key="`emp-${item.id}-${idx}`"
                      :class="{ 'busca-global-highlight': parte.match }"
                    >
                      {{ parte.text }}
                    </span>
                  </div>
                  <div class="busca-global-item-meta">
                    <span v-if="item.cadastroFederal">
                      CNPJ: {{ item.cadastroFederal }}
                    </span>
                    <span v-if="item.telefone">
                      Tel: {{ item.telefone }}
                    </span>
                    <span v-if="item.email">
                      {{ item.email }}
                    </span>
                  </div>
                  <div
                    v-if="item.cidadeEstado"
                    class="busca-global-item-sub"
                  >
                    {{ item.cidadeEstado }}
                  </div>
                </template>

                <template v-else-if="item.tipo === 'pessoa'">
                  <div class="busca-global-item-titulo">
                    <span
                      v-for="(parte, idx) in partesDestaque(item.nome)"
                      :key="`pes-${item.id}-${idx}`"
                      :class="{ 'busca-global-highlight': parte.match }"
                    >
                      {{ parte.text }}
                    </span>
                  </div>
                  <div class="busca-global-item-meta">
                    <span v-if="item.email">
                      {{ item.email }}
                    </span>
                    <span v-if="item.telefone">
                      Tel: {{ item.telefone }}
                    </span>
                    <span v-if="item.celular">
                      Cel: {{ item.celular }}
                    </span>
                  </div>
                </template>

                <template v-else-if="item.tipo === 'proposta'">
                  <div class="busca-global-item-titulo">
                    <span
                      v-for="(parte, idx) in partesDestaque(item.numeroProposta || 'Proposta')"
                      :key="`pro-${item.id}-${idx}`"
                      :class="{ 'busca-global-highlight': parte.match }"
                    >
                      {{ parte.text }}
                    </span>
                  </div>
                  <div class="busca-global-item-meta">
                    <span v-if="item.nomePessoa">
                      {{ item.nomePessoa }}
                    </span>
                    <span v-if="item.statusProposta">
                      {{ item.statusProposta }}
                    </span>
                    <span v-if="item.dataProposta">
                      {{ item.dataProposta }}
                    </span>
                  </div>
                </template>

                <template v-else-if="item.tipo === 'jornada'">
                  <div class="busca-global-item-titulo">
                    <span
                      v-for="(parte, idx) in partesDestaque(item.title || 'Jornada')"
                      :key="`jor-${item.id}-${idx}`"
                      :class="{ 'busca-global-highlight': parte.match }"
                    >
                      {{ parte.text }}
                    </span>
                  </div>
                  <div class="busca-global-item-meta">
                    <span v-if="item.nomePessoa">
                      {{ item.nomePessoa }}
                    </span>
                    <span v-if="item.nomeEmpresa">
                      {{ item.nomeEmpresa }}
                    </span>
                    <span v-if="item.stageName">
                      {{ item.stageName }}
                    </span>
                  </div>
                </template>

                <template v-else-if="item.tipo === 'contrato'">
                  <div class="busca-global-item-titulo">
                    <span
                      v-for="(parte, idx) in partesDestaque(item.numeroProposta || 'Contrato')"
                      :key="`con-${item.id}-${idx}`"
                      :class="{ 'busca-global-highlight': parte.match }"
                    >
                      {{ parte.text }}
                    </span>
                  </div>
                  <div class="busca-global-item-meta">
                    <span v-if="item.nomeEmpresa">
                      {{ item.nomeEmpresa }}
                    </span>
                    <span v-if="item.status">
                      {{ item.status }}
                    </span>
                    <span v-if="item.dataCriacao">
                      {{ item.dataCriacao }}
                    </span>
                  </div>
                </template>
              </li>
            </template>
          </ul>
        </div>

        <div
          v-else-if="mostrarHintMinimo"
          class="busca-global-status"
        >
          Digite pelo menos 3 caracteres.
        </div>

        <div
          v-else
          class="busca-global-status"
        >
          Digite para buscar.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BFormInput, BSpinner, BButton,
} from 'bootstrap-vue'
import {
  ref, reactive, computed, watch, onMounted, onBeforeUnmount,
} from '@vue/composition-api'
import originalAxios from 'axios'
import router from '@/router'
import buscaGlobalApi from './services/buscaGlobalApi'

export default {
  components: {
    BFormInput,
    BSpinner,
    BButton,
  },
  setup() {
    // Definição dos filtros disponíveis com ícones
    const filtrosDisponiveis = [
      {
        key: 'cadastros', label: 'Cadastros', icon: 'UsersIcon', tipos: ['empresa', 'pessoa'],
      },
      {
        key: 'propostas', label: 'Propostas', icon: 'FileTextIcon', tipos: ['proposta'],
      },
      {
        key: 'jornadas', label: 'Jornadas', icon: 'TargetIcon', tipos: ['jornada'],
      },
      {
        key: 'contrato', label: 'Contrato', icon: 'ClipboardIcon', tipos: ['contrato'],
      },
    ]
    const estados = {
      IDLE: 'idle',
      DIGITANDO: 'digitando',
      CARREGANDO: 'carregando',
      RESULTADOS: 'resultados',
      VAZIO: 'vazio',
      ERRO: 'erro',
    }

    const containerRef = ref(null)
    const termoBusca = ref('')
    const resultados = ref(null)
    const estado = ref(estados.IDLE)
    const mensagemErro = ref('')
    const painelAberto = ref(false)
    const itemAtivoIndex = ref(-1)
    let debounceTimer = null
    let cancelTokenSource = null

    const filtrosDefault = {
      filtrosAtivos: ['cadastros', 'propostas'],
    }

    const carregarFiltros = () => {
      const salvos = localStorage.getItem('buscaGlobal_filtros')
      if (salvos) {
        try {
          return JSON.parse(salvos)
        } catch (e) {
          return null
        }
      }
      return null
    }

    // Declarar filtros ANTES de usá-lo nas funções
    const filtros = reactive(carregarFiltros() || { ...filtrosDefault })

    // Verifica se um filtro está ativo
    const isFiltroAtivo = key => filtros.filtrosAtivos.includes(key)

    // Toggle de filtro (liga/desliga)
    const toggleFiltro = key => {
      const index = filtros.filtrosAtivos.indexOf(key)
      if (index > -1) {
        // Remove o filtro se já estiver ativo (mas mantém pelo menos 1)
        if (filtros.filtrosAtivos.length > 1) {
          filtros.filtrosAtivos.splice(index, 1)
        }
      } else {
        // Adiciona o filtro
        filtros.filtrosAtivos.push(key)
      }
    }

    // Converte filtros ativos para tipos de entidade para a API
    const getTiposParaAPI = () => {
      const tipos = []
      filtros.filtrosAtivos.forEach(key => {
        const filtro = filtrosDisponiveis.find(f => f.key === key)
        if (filtro) {
          tipos.push(...filtro.tipos)
        }
      })
      return [...new Set(tipos)] // Remove duplicados
    }

    const abrirPainel = () => {
      painelAberto.value = true
    }

    const fecharPainel = () => {
      painelAberto.value = false
      itemAtivoIndex.value = -1
    }

    const limparBusca = () => {
      termoBusca.value = ''
      resultados.value = null
      estado.value = estados.IDLE
      mensagemErro.value = ''
    }

    const mostrarHintMinimo = computed(() => {
      const tamanho = termoBusca.value.trim().length
      return tamanho > 0 && tamanho < 3
    })

    const showResultados = computed(() => (
      estado.value === estados.RESULTADOS
      || estado.value === estados.VAZIO
      || estado.value === estados.ERRO
      || estado.value === estados.CARREGANDO
    ))

    const gruposComIndex = computed(() => {
      const groups = [
        { tipo: 'empresa', label: 'Empresas', items: resultados.value?.empresas || [] },
        { tipo: 'pessoa', label: 'Pessoas', items: resultados.value?.pessoas || [] },
        { tipo: 'proposta', label: 'Propostas', items: resultados.value?.propostas || [] },
        { tipo: 'jornada', label: 'Jornadas', items: resultados.value?.jornadas || [] },
        { tipo: 'contrato', label: 'Contratos', items: resultados.value?.contratos || [] },
      ]

      let index = 0
      return groups.map(grupo => ({
        ...grupo,
        items: grupo.items.map(item => {
          const currentIndex = index
          index += 1
          return {
            ...item,
            index: currentIndex,
            tipo: grupo.tipo,
            uniqueId: `${grupo.tipo}-${item.id}`,
          }
        }),
      }))
    })

    const todosResultados = computed(() => gruposComIndex.value.flatMap(grp => grp.items))

    const executarBusca = async () => {
      const termo = termoBusca.value.trim()

      if (termo.length < 3) {
        estado.value = estados.IDLE
        resultados.value = null
        mensagemErro.value = ''
        return
      }

      const tiposParaAPI = getTiposParaAPI()
      if (!tiposParaAPI.length) {
        estado.value = estados.ERRO
        mensagemErro.value = 'Selecione ao menos um filtro.'
        resultados.value = null
        return
      }

      if (cancelTokenSource) {
        cancelTokenSource.cancel('Nova busca iniciada')
      }

      cancelTokenSource = originalAxios.CancelToken.source()
      estado.value = estados.CARREGANDO
      abrirPainel()

      try {
        const response = await buscaGlobalApi.buscar({
          termo,
          tiposEntidade: tiposParaAPI,
          camposBusca: ['nome', 'telefone', 'email'],
          correspondenciaExata: false,
          limitePorTipo: 5,
        }, cancelTokenSource.token)

        resultados.value = response.data
        estado.value = response.data.totalResultados > 0
          ? estados.RESULTADOS
          : estados.VAZIO
        itemAtivoIndex.value = response.data.totalResultados > 0 ? 0 : -1
      } catch (error) {
        if (!originalAxios.isCancel(error)) {
          estado.value = estados.ERRO
          mensagemErro.value = 'Erro ao buscar. Tente novamente.'
        }
      }
    }

    watch(filtros, novosFiltros => {
      localStorage.setItem('buscaGlobal_filtros', JSON.stringify(novosFiltros))
      if (termoBusca.value.trim().length >= 3) {
        executarBusca()
      }
    }, { deep: true })

    watch(termoBusca, () => {
      if (debounceTimer) clearTimeout(debounceTimer)

      if (termoBusca.value.trim().length < 3) {
        estado.value = estados.IDLE
        resultados.value = null
        itemAtivoIndex.value = -1
        return
      }

      estado.value = estados.DIGITANDO
      debounceTimer = setTimeout(() => {
        executarBusca()
      }, 300)
    })

    const navegarResultados = direcao => {
      const total = todosResultados.value.length
      if (!total) return

      if (direcao === 'down') {
        itemAtivoIndex.value = (itemAtivoIndex.value + 1) % total
      } else {
        itemAtivoIndex.value = itemAtivoIndex.value <= 0
          ? total - 1
          : itemAtivoIndex.value - 1
      }

      const elemento = document.getElementById(`resultado-${itemAtivoIndex.value}`)
      if (elemento) {
        elemento.scrollIntoView({ block: 'nearest' })
      }
    }

    const navegarPara = (tipo, id) => {
      fecharPainel()
      limparBusca()

      const rotas = {
        empresa: { name: 'editar-emp', params: { id }, paramKey: 'id' },
        pessoa: { name: 'editar-pessoa', params: { id }, paramKey: 'id' },
        proposta: { name: 'cadastro-proposta', params: { propostaId: id }, paramKey: 'propostaId' },
        jornada: { name: 'painel-oportunidades', query: { dealId: id }, paramKey: 'dealId' },
        contrato: { name: 'relatoriocontrato', params: { id }, paramKey: 'id' },
      }

      const rota = rotas[tipo]
      if (rota) {
        const { paramKey = 'id', ...destino } = rota
        const rotaAtual = router.currentRoute
        const mesmaRota = rotaAtual.name === destino.name
        const mesmoId = rotaAtual.params?.[paramKey] === String(id)

        if (mesmaRota && mesmoId) {
          // Se é exatamente a mesma rota e mesmo ID, apenas fecha o painel
          return
        }

        if (mesmaRota && !mesmoId) {
          // Se é a mesma rota mas ID diferente, adiciona timestamp para forçar reload
          const rotaComTimestamp = {
            ...destino,
            query: { ...destino.query, _t: Date.now() },
          }
          router.replace(rotaComTimestamp).catch(() => {})
        } else {
          router.push(destino).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              // ignore
            }
          })
        }
      }
    }

    const selecionarResultadoAtual = () => {
      const item = todosResultados.value[itemAtivoIndex.value]
      if (item) {
        navegarPara(item.tipo, item.id)
      }
    }

    const escapeRegExp = texto => texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const partesDestaque = texto => {
      const termo = termoBusca.value.trim()
      if (!texto) return []
      if (!termo) return [{ text: texto, match: false }]

      const regex = new RegExp(`(${escapeRegExp(termo)})`, 'ig')
      return texto.split(regex).filter(Boolean).map(parte => ({
        text: parte,
        match: parte.toLowerCase() === termo.toLowerCase(),
      }))
    }

    const handleClickOutside = event => {
      if (!containerRef.value) return
      if (!containerRef.value.contains(event.target)) {
        fecharPainel()
      }
    }

    onMounted(() => {
      document.addEventListener('mousedown', handleClickOutside)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleClickOutside)
    })

    return {
      estados,
      termoBusca,
      resultados,
      estado,
      mensagemErro,
      painelAberto,
      filtros,
      filtrosDisponiveis,
      isFiltroAtivo,
      toggleFiltro,
      itemAtivoIndex,
      gruposComIndex,
      todosResultados,
      mostrarHintMinimo,
      showResultados,
      abrirPainel,
      fecharPainel,
      limparBusca,
      executarBusca,
      navegarResultados,
      selecionarResultadoAtual,
      navegarPara,
      partesDestaque,
      containerRef,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.busca-global {
  position: relative;
  width: 60%;
  max-width: 520px;
  min-width: 360px;
  flex: 1;
}

.busca-global-input {
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 20px;
  overflow: hidden;

  &.is-panel-open {
    .busca-global-control {
      border-radius: 20px;
    }
  }
}

.busca-global-control {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  height: 38px;
  border-radius: 20px;
}

.busca-global-icon {
  position: absolute;
  left: 0.75rem;
  color: $text-muted;
  z-index: 2;
}

.busca-global-clear {
  position: absolute;
  right: 0.75rem;
  color: $text-muted;
  cursor: pointer;
  z-index: 2;
}

.busca-global-panel {
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  width: 100%;
  background: $white;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(34, 41, 47, 0.14);
  z-index: 10;
  overflow: hidden;
  border: 1px solid rgba(34, 41, 47, 0.08);
  padding-top: 6px;

  .dark-layout & {
    background: $theme-dark-card-bg;
    border-color: rgba(255, 255, 255, 0.08);
  }
}

.busca-global-filtros {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem 0.75rem;
  border-bottom: 1px solid rgba(34, 41, 47, 0.08);

  .dark-layout & {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
}

.busca-filtro-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #6e6b7b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--primary-rgb), 0.12);
    color: var(--primary);
  }

  &.is-active {
    background: var(--primary);
    color: white;
  }

  .dark-layout & {
    color: #b4b7bd;

    &:hover {
      background: rgba(var(--primary-rgb), 0.24);
      color: var(--primary);
    }

    &.is-active {
      background: var(--primary);
      color: white;
    }
  }
}

.busca-global-label {
  font-size: 0.875rem;
  color: $text-muted;
}

.busca-global-resultados {
  max-height: 420px;
  overflow-y: auto;
}

.busca-global-status {
  padding: 1rem;
  text-align: center;
  color: $text-muted;
}

.busca-global-erro {
  color: $danger;
}

.busca-global-lista {
  font-size: 0.9rem;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
}

.busca-global-grupo-titulo {
  position: sticky;
  top: 0;
  padding: 0.6rem 1rem 0.4rem;
  font-weight: 700;
  color: #4b4b4b;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  background: $white;
  border-bottom: 1px solid rgba(34, 41, 47, 0.06);
  box-shadow: 0 1px 0 rgba(34, 41, 47, 0.04);
  z-index: 1;

  .dark-layout & {
    color: #e0e0e0;
    background: $theme-dark-card-bg;
    border-bottom-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04);
  }
}

.busca-global-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-top: 1px solid rgba(34, 41, 47, 0.06);

  .dark-layout & {
    border-top-color: rgba(255, 255, 255, 0.06);
  }

  &.is-active {
    background: rgba(115, 103, 240, 0.12);
  }
}

.busca-global-item-titulo {
  font-weight: 600;
  font-size: 0.95rem;
}

.busca-global-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #7a7a7a;

  .dark-layout & {
    color: #b4b7bd;
  }
}

.busca-global-item-sub {
  font-size: 0.85rem;
  color: #7a7a7a;

  .dark-layout & {
    color: #b4b7bd;
  }
}

mark {
  background: rgba(255, 193, 7, 0.35);
  padding: 0 2px;
}

.busca-global-highlight {
  background: rgba(255, 193, 7, 0.35);
  padding: 0 2px;
}
</style>
